// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/cofre/AnaliseFiscalPainel.tsx
//  Painel da análise fiscal de uma competência: Simples Nacional (receita, RBT12,
//  faixa, alíquota efetiva, DAS estimado), alertas de inconsistência e os resumos por
//  CFOP, CST do ICMS, PIS/COFINS e NCM. Usado no Portal do Contador e na Central
//  Fiscal do ERP — quem usa passa a função que busca os dados (cada lado tem seu cliente HTTP).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import { ANEXOS_SIMPLES, type AnaliseFiscal, type SentidoCofre } from './analiseFiscal';

const SUGERIDO = '__sugerido__';

interface Props {
  /** Chave de cache (inclua quem é o contribuinte). */
  chave: unknown[];
  competencia: number;
  carregar: (competencia: number, anexo?: number) => Promise<AnaliseFiscal>;
}

/** "out/25" */
const rotuloMes = (c: number) =>
  `${new Date(Math.floor(c / 100), (c % 100) - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}/${String(Math.floor(c / 100)).slice(2)}`;
const pct = (v: number) => `${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const sentido = (s: SentidoCofre) => (s === 1 ? 'Saída' : 'Entrada');

export function AnaliseFiscalPainel({ chave, competencia, carregar }: Props) {
  const [anexo, setAnexo] = useState<number | undefined>();
  const { data, isLoading, error } = useQuery({
    queryKey: [...chave, 'analise', competencia, anexo],
    queryFn: () => carregar(competencia, anexo),
    retry: false,
  });

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-64" /></div>;
  if (error || !data)
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>{error instanceof Error ? error.message : 'Análise indisponível.'}</AlertDescription>
      </Alert>
    );

  const s = data.simples;
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <div>
              <CardTitle className="text-base">Simples Nacional</CardTitle>
              <CardDescription>Estimativa a partir das notas do cofre — confira antes de gerar o DAS.</CardDescription>
            </div>
            <Select value={anexo ? String(anexo) : SUGERIDO} onValueChange={(v) => setAnexo(v === SUGERIDO ? undefined : Number(v))}>
              <SelectTrigger className="sm:ml-auto w-full sm:w-80"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SUGERIDO}>Sugerido: {ANEXOS_SIMPLES[s.anexoSugerido]}</SelectItem>
                {Object.entries(ANEXOS_SIMPLES).map(([n, nome]) => <SelectItem key={n} value={n}>{nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!s.aplicavel && (
            <Alert><Info className="h-4 w-4" /><AlertDescription>{s.observacao}</AlertDescription></Alert>
          )}
          {s.alertaLimite && (
            <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>{s.alertaLimite}</AlertDescription></Alert>
          )}
          <div className="grid gap-3 grid-cols-2 md:grid-cols-3 2xl:grid-cols-5">
            <Kpi titulo="Receita do mês" valor={formatCurrency(s.receitaMes)} />
            <Kpi titulo="RBT12 (12 meses anteriores)" valor={formatCurrency(s.rbt12)} />
            <Kpi titulo={`Faixa ${s.faixa} · nominal`} valor={pct(s.aliquotaNominal)} />
            <Kpi titulo="Alíquota efetiva" valor={pct(s.aliquotaEfetiva)} />
            <Kpi titulo="DAS estimado" valor={formatCurrency(s.dasEstimado)} destaque />
          </div>
          <p className="text-xs text-muted-foreground">
            {s.nomeAnexo}{anexo ? '' : ` (sugerido: ${s.motivoSugestao})`}.
            {s.aplicavel && s.observacao ? ` ${s.observacao}` : ''}{' '}
            Não segrega ST, monofásico nem ISS retido, e não calcula o Fator R.
          </p>
          <div>
            <p className="text-sm font-medium mb-2">Receita líquida por mês (vendas − devoluções)</p>
            {/* Grade que quebra linha: 13 meses lado a lado não cabem numa tela estreita. */}
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 2xl:grid-cols-[repeat(13,minmax(0,1fr))] gap-2">
              {s.historico.map((h) => (
                <div key={h.competencia}
                  className={`rounded-md border px-2 py-1.5 ${h.competencia === data.competencia ? 'border-primary' : ''}`}>
                  <p className="text-xs text-muted-foreground">{rotuloMes(h.competencia)}</p>
                  <p className="text-xs font-medium whitespace-nowrap">{h.liquida ? formatCurrency(h.liquida) : '—'}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Pontos de atenção</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {data.alertas.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Nenhuma inconsistência encontrada nas saídas desta competência.
            </p>
          ) : data.alertas.map((a) => (
            <Alert key={a.tipo}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="flex items-center gap-2">{a.titulo} <Badge variant="secondary">{a.quantidade}</Badge></AlertTitle>
              <AlertDescription className="space-y-1">
                <p>{a.orientacao}</p>
                <ul className="text-xs text-muted-foreground list-disc pl-4">
                  {a.exemplos.map((e, i) => <li key={i}>Nota {e.numero}: {e.detalhe}</li>)}
                  {a.quantidade > a.exemplos.length && <li>e mais {a.quantidade - a.exemplos.length}…</li>}
                </ul>
              </AlertDescription>
            </Alert>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="cfop">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="cfop">Por CFOP</TabsTrigger>
              <TabsTrigger value="cst">CST/CSOSN do ICMS</TabsTrigger>
              <TabsTrigger value="pis">PIS/COFINS</TabsTrigger>
              <TabsTrigger value="ncm">NCM (saídas)</TabsTrigger>
            </TabsList>
            <TabsContent value="cfop" className="overflow-x-auto">
              <Tabela vazio={!data.cfop.length} cabecalho={['Sentido', 'CFOP', 'Notas', 'Itens', 'Valor', 'Base ICMS', 'ICMS', 'ST', 'IPI']}>
                {data.cfop.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>{sentido(l.sentido)}</TableCell><TableCell className="font-mono">{l.cfop ?? '—'}</TableCell>
                    <Num v={l.documentos} /><Num v={l.itens} /><Din v={l.valor} /><Din v={l.baseIcms} /><Din v={l.icms} /><Din v={l.st} /><Din v={l.ipi} />
                  </TableRow>
                ))}
              </Tabela>
            </TabsContent>
            <TabsContent value="cst" className="overflow-x-auto">
              <Tabela vazio={!data.cstIcms.length} cabecalho={['Sentido', 'CST/CSOSN', 'Itens', 'Valor', 'Base ICMS', 'ICMS', 'ST']}>
                {data.cstIcms.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>{sentido(l.sentido)}</TableCell><TableCell className="font-mono">{l.cst ?? '—'}</TableCell>
                    <Num v={l.itens} /><Din v={l.valor} /><Din v={l.baseIcms} /><Din v={l.icms} /><Din v={l.st} />
                  </TableRow>
                ))}
              </Tabela>
            </TabsContent>
            <TabsContent value="pis" className="overflow-x-auto">
              <Tabela vazio={!data.pisCofins.length} numericasDesde={3} cabecalho={['Sentido', 'CST PIS', 'CST COFINS', 'Itens', 'Valor', 'PIS', 'COFINS']}>
                {data.pisCofins.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>{sentido(l.sentido)}</TableCell><TableCell className="font-mono">{l.cstPis ?? '—'}</TableCell>
                    <TableCell className="font-mono">{l.cstCofins ?? '—'}</TableCell>
                    <Num v={l.itens} /><Din v={l.valor} /><Din v={l.pis} /><Din v={l.cofins} />
                  </TableRow>
                ))}
              </Tabela>
            </TabsContent>
            <TabsContent value="ncm" className="overflow-x-auto">
              <Tabela vazio={!data.ncm.length} numericasDesde={4} cabecalho={['NCM', 'Produto (exemplo)', 'CST/CSOSN', 'CST PIS', 'Itens', 'Valor']}>
                {data.ncm.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono">{l.ncm ?? '—'}</TableCell>
                    <TableCell className="max-w-72 truncate">{l.descricao ?? '—'}</TableCell>
                    <TableCell className="font-mono">{l.cstIcms ?? '—'}</TableCell><TableCell className="font-mono">{l.cstPis ?? '—'}</TableCell>
                    <Num v={l.itens} /><Din v={l.valor} />
                  </TableRow>
                ))}
              </Tabela>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ titulo, valor, destaque = false }: { titulo: string; valor: string; destaque?: boolean }) {
  return (
    <div className={`rounded-md border p-3 ${destaque ? 'border-primary' : ''}`}>
      <p className="text-base sm:text-lg font-semibold break-words">{valor}</p>
      <p className="text-xs text-muted-foreground">{titulo}</p>
    </div>
  );
}

function Tabela({ cabecalho, vazio, numericasDesde = 2, children }: {
  cabecalho: string[]; vazio: boolean; numericasDesde?: number; children: React.ReactNode;
}) {
  if (vazio) return <p className="py-6 text-sm text-muted-foreground">Nenhum item autorizado nesta competência.</p>;
  return (
    <Table>
      <TableHeader>
        <TableRow>{cabecalho.map((c, i) => <TableHead key={c} className={i >= numericasDesde ? 'text-right' : ''}>{c}</TableHead>)}</TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  );
}

const Num = ({ v }: { v: number }) => <TableCell className="text-right">{v.toLocaleString('pt-BR')}</TableCell>;
const Din = ({ v }: { v: number }) => <TableCell className="text-right whitespace-nowrap">{formatCurrency(v)}</TableCell>;
